import { Broadcast, Channel, Message, Platform, Scene, UserStudio } from "../features/api/apiSlice";

export enum SocketApiActionType {
  "BROADCAST_STATUS_UPDATED" = "BROADCAST_STATUS_UPDATED",
  "CHANNEL_STATUS_UPDATED" = "TEMPLATE_STATUS_UPDATED",
  "TWITCH_BITRATE_WARNING" = "TWITCH_BITRATE_WARNING",
  "BITRATE_WARNING" = "BITRATE_WARNING",
  "NEW_CHAT_MESSAGE" = "MULTICHAT_MESSAGE",
  "VK_CAPTCHA_ERROR" = "VK_CAPTCHA_ERROR",
  "VK_FLOOD_ERROR" = "VK_FLOOD_ERROR",
  "VK_UNKNOWN_ERROR" = "VK_UNKNOWN_ERROR",
  "SEND_NEW_CHAT_MESSAGE" = "SEND_MULTICHAT_MESSAGE",
  "STUDIO_SCENE_UPDATED" = "STUDIO_SCENE_UPDATED",
  "INVALID_STREAM_KEY_WARNING" = "INVALID_STREAM_KEY",
  "TARIFF_ENDED" = "TARIFF_ENDED",
  "STUDIO_UPDATED" = "STUDIO_UPDATED",
  "UPDATE_STUDIO_DATA" = "UPDATE_STUDIO_DATA",
  "TELEGRAM_CHAT_LINKED" = "TELEGRAM_CHAT_LINKED",
  "UPDATE_CHANNEL_UPTIME" = "UPDATE_CHANNEL_UPTIME",
  "NEXT_TEMPLATE_RESTART_TIME" = "NEXT_TEMPLATE_RESTART_TIME"
}

export type SocketApiParams = {
  channelId?: number;
  broadcastId?: number;
  studioId?: number;
  onBroadcastStatusUpdated?: (payload: BroadcastStatusUpdatedAction["payload"]) => void;
  onChannelStatusUpdated?: (payload: ChannelStatusUpdated["payload"]) => void;
  onTwitchBitrateWarning?: (payload: TwitchBitrateWarningAction["payload"]) => void;
  onBitrateWarning?: (payload: BitrateWarningAction["payload"]) => void;
  onNewChatMessage?: (payload: ChatMessageAction["payload"]) => void;
  onVkCaptchaError?: (payload: VkCaptchaErrorAction["payload"]) => void;
  onVkFloodError?: (payload: VkFloodErrorAction["payload"]) => void;
  onVkUnknowError?: (payload: VkUnknownErrorAction["payload"]) => void;
  onStudioSceneUpdated?: (payload: StudioSceneUpdated["payload"]) => void;
  onInvalidStreamKeyWarning?: (payload: InvalidStreamKeyWarning["payload"]) => void;
  onCheckTariffEnded?: (payload: CheckTariffEnded["payload"]) => void;
  onStudioUpdated?: (payload: StudioUpdated["payload"]) => void;
  onStudioUpdateStatus?: (payload: StudioUpdateStatus["payload"]) => void;
  onTelegramLinkedSuccess?: (payload: TelegramLinkedSuccessAction["payload"]) => void;
  onUpdateChannelUptime?: (payload: UpdateChannelUptime["payload"]) => void;
  onNextTemplateRestartTime?: (payload: NextTemplateRestartTime["payload"]) => void;
};

export interface BroadcastStatusUpdatedAction {
  action: SocketApiActionType.BROADCAST_STATUS_UPDATED;
  payload: {
    broadcast_id: number;
    status: Broadcast["status"];
    m3u8_url: string;
    uptime: number;
    bitrates: number[];
    server: string;
  };
}

export interface StudioSceneUpdated{
  action: SocketApiActionType.STUDIO_SCENE_UPDATED;
  payload: {
    studio_id: number;
    studio_scene: Scene;
  };
}

export interface CheckTariffEnded {
  action: SocketApiActionType.TARIFF_ENDED;
  payload: {
    is_tariff_ended: boolean;
  };
}

export interface StudioUpdated{
  action: SocketApiActionType.STUDIO_UPDATED;
  payload: {
    studio: UserStudio;
  };
}

export interface StudioUpdateStatus{
  action: SocketApiActionType.UPDATE_STUDIO_DATA;
  payload: {
    studio_id: number;
    status: string;
  };
}


export interface ChannelStatusUpdated {
  action: SocketApiActionType.CHANNEL_STATUS_UPDATED;
  payload: {
    restream_template: {
      id: number;
      status: "online" | "offline" | "disabled";
      uptime: number;
      next_restart_time?: Channel["next_restart_time"];
    };
    broadcast_id: number;
  };
}

export interface TwitchBitrateWarningAction {
  action: SocketApiActionType.TWITCH_BITRATE_WARNING;
  payload: {
    error_bitrate: number;
    restream_template_id: number;
    broadcast_id: Broadcast['id'];
    error: boolean;
  };
}

export interface BitrateWarningAction {
  action: SocketApiActionType.BITRATE_WARNING;
  payload: {
    current_bitrate: number;
    broadcast_id: Broadcast['id'];
    error: boolean;
  };
}

export interface InvalidStreamKeyWarning {
  action: SocketApiActionType.INVALID_STREAM_KEY_WARNING;
  payload: {
    broadcast_id: Broadcast['id'];
    error: boolean;
  };
}

export interface ChatMessageAction {
  action: SocketApiActionType.NEW_CHAT_MESSAGE;
  payload: Message;
}

export interface VkCaptchaErrorAction {
  action: SocketApiActionType.VK_CAPTCHA_ERROR;
  payload: {
    platform: Platform;
    message: string;
    error: {
      error_msg: string;
      captcha_sid: string;
      captcha_img: string;
    };
  };
}

export interface VkFloodErrorAction {
  action: SocketApiActionType.VK_FLOOD_ERROR;
  payload: {
    platform: Platform;
    error: {
      error_msg: string;
    };
  };
}

export interface NextTemplateRestartTime {
  action: SocketApiActionType.NEXT_TEMPLATE_RESTART_TIME;
  payload: {
    broadcast_id: Broadcast["id"];
    template_id: Channel["id"];
    next_restart_time: Channel["next_restart_time"];
  };
}

export interface VkUnknownErrorAction {
  action: SocketApiActionType.VK_UNKNOWN_ERROR;
  payload: {
    platform: Platform;
    error: {
      error_msg: string;
    };
  };
}

export interface TelegramLinkedSuccessAction {
  action: SocketApiActionType.TELEGRAM_CHAT_LINKED;
  payload: {};
}

interface SendMessageParams {
  identifier: string;
}

export interface SendChatMessageAction extends SendMessageParams {
  action: SocketApiActionType.SEND_NEW_CHAT_MESSAGE;
  payload: {
    message?: Message['message'];
    captcha_sid?: string;
    captcha_key?: string;
    retry_platform?: Platform;
    broadcast_id?: number;
  };
  target_platform?: Message['target_platform'];
}

export interface UpdateChannelUptime {
  action: SocketApiActionType.UPDATE_CHANNEL_UPTIME;
  payload: {
    broadcast_id: number;
    templ_id: number;
    uptime: number;
  };
}

export type SocketAnswerAction =
  | BroadcastStatusUpdatedAction
  | ChannelStatusUpdated
  | TwitchBitrateWarningAction
  | BitrateWarningAction
  | ChatMessageAction
  | VkCaptchaErrorAction
  | VkFloodErrorAction
  | VkUnknownErrorAction
  | StudioSceneUpdated
  | InvalidStreamKeyWarning
  | CheckTariffEnded
  | StudioUpdated
  | StudioUpdateStatus
  | TelegramLinkedSuccessAction
  | UpdateChannelUptime
  | NextTemplateRestartTime;

export type SocketRequestAction =
  | SendChatMessageAction
